import React from 'react';
import { Database, ArrowRight } from 'lucide-react';
import { useDbNavigate, useDatabaseSafe } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';

const modules = [
  {
    slug: 'estimating',
    label: 'Estimating',
    permKey: 'module.estimating',
    description: 'Manage estimating data including instrument and equipment schedules, IO lists, and field device assignments for project cost estimation.'
  },
  {
    slug: 'integration',
    label: 'Integration',
    permKey: 'module.integration',
    description: 'Manage integration data for PLC and SCADA systems including IO tags, network devices, panels, and platform configurations.'
  },
  {
    slug: 'submittals',
    label: 'Submittals',
    permKey: 'module.submittal',
    description: 'Track and manage submittal packages, vendor documentation, approval status, and transmittal records for project deliverables.'
  },
  {
    slug: 'sub-contractors',
    label: 'Sub Contractors',
    permKey: 'module.subcontractor',
    description: 'Manage subcontractor records, scope assignments, contact information, and associated work packages.'
  },
  {
    slug: 'purchasing',
    label: 'Purchasing',
    permKey: 'module.purchasing',
    description: 'Manage purchase orders, material requisitions, vendor assignments, and procurement status tracking.'
  },
  {
    slug: 'qaqc',
    label: 'QA/QC',
    permKey: 'module.qaqc',
    description: 'Manage quality assurance and quality control records including inspection checklists, test results, and punch list items.'
  },
  {
    slug: 'material-transfer',
    label: 'Material Transfer',
    permKey: 'module.transmittal',
    description: 'Track material transfer records, transmittals, and equipment delivery status across project locations.'
  },
  {
    slug: 'commissioning',
    label: 'Commissioning',
    permKey: 'module.commissioning',
    description: 'Manage commissioning activities including loop checks, OAT records, punch lists, and startup status by panel and system.'
  }
];

const DataEntryDashboardPage = () => {
  const navigate = useDbNavigate();
  const { hasPermission, isSysAdmin } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';

  const canAccess = (permKey) => isSysAdmin || hasPermission(permKey, dbSlug);
  const visibleModules = modules.filter(m => canAccess(m.permKey));

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: 'var(--spacing-lg)'
      }}>
        <Database size={20} style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
        <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
          Data Entry
        </h1>
        <span style={{ marginLeft: '12px', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
          {visibleModules.length} {visibleModules.length === 1 ? 'module' : 'modules'} available
        </span>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: 'var(--spacing-md)',
        paddingBottom: 'var(--spacing-sm)',
        borderBottom: '2px solid var(--color-primary)'
      }}>
        <Database size={16} style={{ color: 'var(--color-primary)' }} />
        <span style={{
          fontSize: 'var(--text-base)',
          fontWeight: 700,
          color: 'var(--color-primary)',
          fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Modules
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
          {visibleModules.length} {visibleModules.length === 1 ? 'module' : 'modules'} available
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: 'var(--spacing-md)'
      }}>
        {modules.map(module => {
          const accessible = canAccess(module.permKey);
          return (
            <div
              key={module.slug}
              onClick={() => accessible && navigate(`/${module.slug}`)}
              style={{
                background: 'var(--color-bg-white)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                padding: 'var(--spacing-md) var(--spacing-lg)',
                cursor: accessible ? 'pointer' : 'default',
                opacity: accessible ? 1 : 0.4,
                transition: 'border-color 0.15s, box-shadow 0.15s',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
              onMouseEnter={e => {
                if (accessible) {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {module.label}
                </span>
                {accessible
                  ? <ArrowRight size={16} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />
                  : <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>No access</span>
                }
              </div>
              <p style={{
                margin: 0,
                fontSize: 'var(--text-sm)',
                color: 'var(--color-gray-600)',
                lineHeight: 1.5
              }}>
                {module.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataEntryDashboardPage;
